import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PageHeader from '../components/common/PageHeader';
import { DrawIoEmbed, DrawIoEmbedRef, EventExport } from 'react-drawio';
import {
  drawioService,
  DrawioDiagramListItem,
  DrawioDiagramDetail,
} from '../services/workbenchService';

const AUTOSAVE_DEBOUNCE_MS = 1500;

const WorkbenchDrawioPage: React.FC = () => {
  const theme = useTheme();
  const drawioRef = useRef<DrawIoEmbedRef>(null);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingExportFormat = useRef<string | null>(null);

  const [diagrams, setDiagrams] = useState<DrawioDiagramListItem[]>([]);
  const [activeDiagram, setActiveDiagram] = useState<DrawioDiagramDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'new' | 'rename'>('new');
  const [dialogName, setDialogName] = useState('');

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [selectorAnchor, setSelectorAnchor] = useState<null | HTMLElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // ─── Load diagram list on mount ─────────────────────────────

  const fetchDiagrams = useCallback(async () => {
    try {
      const data = await drawioService.listDiagrams();
      setDiagrams(data);
      return data;
    } catch {
      setSnackMsg('Fehler beim Laden der Diagramme');
      return [];
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await fetchDiagrams();
      if (data.length > 0) {
        try {
          const detail = await drawioService.getDiagram(data[0].id);
          setActiveDiagram(detail);
        } catch {
          // ignore
        }
      }
      setLoading(false);
    })();
  }, [fetchDiagrams]);

  // ─── Select diagram ─────────────────────────────────────────

  const handleSelectDiagram = async (id: number) => {
    setSelectorAnchor(null);
    if (id === activeDiagram?.id) return;
    try {
      const detail = await drawioService.getDiagram(id);
      setActiveDiagram(detail);
    } catch {
      setSnackMsg('Fehler beim Laden des Diagramms');
    }
  };

  // ─── New / Rename ───────────────────────────────────────────

  const handleNewDiagram = () => {
    setDialogMode('new');
    setDialogName('');
    setDialogOpen(true);
  };

  const handleRenameDiagram = () => {
    if (!activeDiagram) return;
    setDialogMode('rename');
    setDialogName(activeDiagram.name);
    setDialogOpen(true);
  };

  const handleDialogConfirm = async () => {
    const trimmed = dialogName.trim();
    if (!trimmed) return;

    try {
      if (dialogMode === 'new') {
        const created = await drawioService.createDiagram(trimmed);
        setActiveDiagram(created);
        await fetchDiagrams();
        setSnackMsg(`"${trimmed}" erstellt`);
      } else if (dialogMode === 'rename' && activeDiagram) {
        const updated = await drawioService.updateDiagram(activeDiagram.id, { name: trimmed });
        setActiveDiagram(updated);
        await fetchDiagrams();
        setSnackMsg(`Umbenannt zu "${trimmed}"`);
      }
    } catch {
      setSnackMsg('Fehler beim Speichern');
    }
    setDialogOpen(false);
  };

  // ─── Delete ─────────────────────────────────────────────────

  const handleDeleteDiagram = async () => {
    if (!activeDiagram) return;
    const name = activeDiagram.name;
    try {
      await drawioService.deleteDiagram(activeDiagram.id);
      const remaining = await fetchDiagrams();
      if (remaining.length > 0) {
        const detail = await drawioService.getDiagram(remaining[0].id);
        setActiveDiagram(detail);
      } else {
        setActiveDiagram(null);
      }
      setSnackMsg(`"${name}" gelöscht`);
    } catch {
      setSnackMsg('Fehler beim Löschen');
    }
    setDeleteConfirmOpen(false);
  };

  // ─── Autosave (debounced) ───────────────────────────────────

  const handleAutoSave = useCallback(
    (data: { xml: string }) => {
      if (!activeDiagram?.id || !data.xml) return;
      const diagramId = activeDiagram.id;

      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);

      autosaveTimer.current = setTimeout(async () => {
        setSaving(true);
        try {
          const updated = await drawioService.updateDiagram(diagramId, { xml: data.xml });
          setActiveDiagram((prev) =>
            prev?.id === diagramId ? { ...prev, xml: data.xml, updated_at: updated.updated_at } : prev,
          );
          setDiagrams((prev) =>
            prev.map((d) => (d.id === diagramId ? { ...d, updated_at: updated.updated_at } : d)),
          );
        } catch {
          // silent - will retry on next autosave
        }
        setSaving(false);
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [activeDiagram?.id],
  );

  useEffect(() => {
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, []);

  // ─── Export ─────────────────────────────────────────────────

  const handleExport = (format: 'png' | 'svg') => {
    setExportAnchor(null);
    if (!drawioRef.current) return;
    pendingExportFormat.current = format;
    drawioRef.current.exportDiagram({
      format: format === 'svg' ? 'xmlsvg' : 'png',
    });
  };

  const handleExportXml = () => {
    setExportAnchor(null);
    if (!activeDiagram?.xml) return;
    downloadFile(
      activeDiagram.xml,
      `${activeDiagram.name}.drawio.xml`,
      'application/xml',
    );
    setSnackMsg('XML exportiert');
  };

  const handleExportCallback = useCallback(
    (data: EventExport) => {
      if (!activeDiagram) return;
      const fmt = pendingExportFormat.current || 'png';
      pendingExportFormat.current = null;

      if (fmt === 'svg') {
        downloadFile(data.data, `${activeDiagram.name}.svg`, 'image/svg+xml');
      } else {
        downloadDataUrl(data.data, `${activeDiagram.name}.png`);
      }
      setSnackMsg(`${fmt.toUpperCase()} exportiert`);
    },
    [activeDiagram],
  );

  // ─── Download helpers ───────────────────────────────────────

  function downloadFile(content: string, filename: string, mime: string) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadDataUrl(dataUrl: string, filename: string) {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    a.click();
  }

  // ─── Render ─────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <PageHeader
        title="Draw.io Editor"
        subtitle="Diagramme erstellen und bearbeiten – Netzwerk, Flowcharts, UML und mehr"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'Draw.io Editor' },
        ]}
      />

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0,
          py: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Diagram selector */}
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setSelectorAnchor(e.currentTarget)}
          endIcon={<ExpandMoreIcon />}
          sx={{
            textTransform: 'none',
            minWidth: 180,
            justifyContent: 'space-between',
            fontWeight: 600,
          }}
        >
          {activeDiagram ? activeDiagram.name : 'Kein Diagramm'}
        </Button>

        <Menu
          anchorEl={selectorAnchor}
          open={Boolean(selectorAnchor)}
          onClose={() => setSelectorAnchor(null)}
          slotProps={{ paper: { sx: { minWidth: 220, maxHeight: 360 } } }}
        >
          {diagrams.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Keine Diagramme vorhanden
              </Typography>
            </MenuItem>
          )}
          {diagrams.map((d) => (
            <MenuItem
              key={d.id}
              selected={d.id === activeDiagram?.id}
              onClick={() => handleSelectDiagram(d.id)}
            >
              <ListItemIcon>
                <InsertDriveFileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={d.name}
                secondary={new Date(d.updated_at).toLocaleString('de-DE')}
                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
              />
            </MenuItem>
          ))}
        </Menu>

        <Tooltip title="Neues Diagramm">
          <IconButton size="small" color="primary" onClick={handleNewDiagram}>
            <AddIcon />
          </IconButton>
        </Tooltip>

        {activeDiagram && (
          <>
            <Tooltip title="Umbenennen">
              <IconButton size="small" onClick={handleRenameDiagram}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Exportieren">
              <IconButton
                size="small"
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
            >
              <MenuItem onClick={() => handleExport('png')}>
                <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Als PNG</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleExport('svg')}>
                <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Als SVG</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExportXml}>
                <ListItemIcon><InsertDriveFileIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Als XML</ListItemText>
              </MenuItem>
            </Menu>

            <Tooltip title="Diagramm löschen">
              <IconButton
                size="small"
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{ color: theme.palette.error.main }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {saving && (
                <CircularProgress size={14} sx={{ color: theme.palette.text.secondary }} />
              )}
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                {saving
                  ? 'Speichert...'
                  : `Zuletzt gespeichert: ${new Date(activeDiagram.updated_at).toLocaleString('de-DE')}`}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Editor area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {activeDiagram ? (
          <DrawIoEmbed
            key={activeDiagram.id}
            ref={drawioRef}
            xml={activeDiagram.xml || undefined}
            autosave
            urlParameters={{
              ui: 'kennedy',
              spin: true,
              libraries: true,
              saveAndExit: false,
              noSaveBtn: true,
              noExitBtn: true,
            }}
            onAutoSave={handleAutoSave}
            onExport={handleExportCallback}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2,
              color: theme.palette.text.secondary,
            }}
          >
            <InsertDriveFileIcon sx={{ fontSize: 64, opacity: 0.3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Kein Diagramm ausgewählt
            </Typography>
            <Typography variant="body2">
              Erstelle ein neues Diagramm, um loszulegen.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewDiagram}
            >
              Neues Diagramm
            </Button>
          </Box>
        )}
      </Box>

      {/* New / Rename Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'new' ? 'Neues Diagramm' : 'Diagramm umbenennen'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleDialogConfirm()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleDialogConfirm}
            disabled={!dialogName.trim()}
          >
            {dialogMode === 'new' ? 'Erstellen' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Diagramm löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            "{activeDiagram?.name}" wird unwiderruflich gelöscht.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteDiagram}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackMsg)}
        autoHideDuration={2500}
        onClose={() => setSnackMsg('')}
        message={snackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkbenchDrawioPage;
